import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import type { TelemetryAggregateDto, TelemetryPercentilesDto } from '@signalvote/shared';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { useAssistantChargeQuestions, useAssistantTelemetryOverview } from '@/hooks/queries';
import { FilterSelect } from './FilterSelect';
import { TelemetryRunList } from './TelemetryRunList';
import { answeredLabel, campaignOptions, countsLabel, formatCost, formatCount, formatMs, groupDays, pushesLabel } from './telemetryDisplay';

const percentiles = (p: TelemetryPercentilesDto) => `${formatMs(p.median)} / ${formatMs(p.p95)}`;

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground tabular-nums">{value}</div>
    </div>
  );
}

function Totals({ totals }: { totals: TelemetryAggregateDto }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      <Stat label="Runs" value={formatCount(totals.runs)} />
      <Stat label="Failures" value={formatCount(totals.failures)} />
      <Stat label="Server first text p50 / p95" value={percentiles(totals.firstTextMs)} />
      <Stat label="Client first text p50 / p95" value={percentiles(totals.clientFirstTextMs)} />
      <Stat label="Client done p50 / p95" value={percentiles(totals.clientDoneMs)} />
      <Stat label="Tokens in / out" value={`${formatCount(totals.inputTokens)} / ${formatCount(totals.outputTokens)}`} />
      <Stat label="Cached / reasoning tokens" value={`${formatCount(totals.cachedInputTokens)} / ${formatCount(totals.reasoningTokens)}`} />
      <Stat label="Cost (est.)" value={formatCost(totals.estimatedCostUsd)} />
      <Stat label="Thumbs up / down" value={`${totals.thumbsUp} / ${totals.thumbsDown}`} />
      <Stat label="Pushes sent / failed / deferred" value={pushesLabel(totals)} />
    </div>
  );
}

function ChargeQuestions({ from, to, campaignId }: { from?: string; to?: string; campaignId?: string }) {
  const counts = useAssistantChargeQuestions({ from, to, campaignId });
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold text-foreground">Charge questions</h2>
      {counts.isError ? (
        <p className="text-sm text-destructive">Could not load the charge questions.</p>
      ) : !counts.data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label="Bank lines asked about" value={formatCount(counts.data.asked)} />
          <Stat label="Answered (explained or recorded)" value={answeredLabel(counts.data)} />
          <Stat label="Unanswered after 7 days" value={formatCount(counts.data.unansweredAt7Days)} />
        </div>
      )}
      <p className="text-xs text-muted-foreground">Each bank line counts once, on the UTC day it was first asked about.</p>
    </section>
  );
}

export function AssistantTelemetryPage() {
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();
  const [campaignId, setCampaignId] = useState<string>();
  const overview = useAssistantTelemetryOverview({ from, to, campaignId });
  // The choices come from the unfiltered overview, so picking one campaign doesn't hide the others.
  const unfiltered = useAssistantTelemetryOverview({ from, to });
  const effectiveFrom = from ?? overview.data?.from;
  const effectiveTo = to ?? overview.data?.to;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Assistant telemetry</h1>
        <p className="text-muted-foreground mt-1">
          Speed, cost, failures and feedback for the assistant across every campaign in this subsidiary. Days are
          campaign-local.{' '}
          <Link to="/admin/assistant-test" className="underline">
            Open the test bench
          </Link>
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>From</span>
          <Input type="date" value={effectiveFrom ?? ''} onChange={(e) => setFrom(e.target.value || undefined)} />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>To</span>
          <Input type="date" value={effectiveTo ?? ''} onChange={(e) => setTo(e.target.value || undefined)} />
        </label>
        <FilterSelect
          label="Campaign"
          value={campaignId}
          onChange={setCampaignId}
          options={campaignOptions(unfiltered.data?.days ?? [])}
        />
      </div>

      {overview.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading telemetry…
        </div>
      ) : overview.isError || !overview.data ? (
        <div className="text-sm text-destructive">Could not load assistant telemetry.</div>
      ) : (
        <>
          <Totals totals={overview.data.totals} />
          <section className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">By day</h2>
            {overview.data.days.length === 0 ? (
              <p className="text-sm text-muted-foreground">No runs or pushes in this range.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Day</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Runs</TableHead>
                    <TableHead>Triggers</TableHead>
                    <TableHead>Outcomes</TableHead>
                    <TableHead className="text-right">First text p50</TableHead>
                    <TableHead className="text-right">Client done p50</TableHead>
                    <TableHead className="text-right">Tokens in / out</TableHead>
                    <TableHead className="text-right">Cost (est.)</TableHead>
                    <TableHead className="text-right">Up / down</TableHead>
                    <TableHead className="text-right">Pushes s / f / d</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupDays(overview.data.days).flatMap((group) =>
                    group.rows.map((row, i) => {
                      const a = row.aggregate;
                      return (
                        <TableRow key={`${row.localDay}:${row.campaignId}`}>
                          {i === 0 && (
                            <TableCell rowSpan={group.rows.length} className="align-top font-medium tabular-nums">
                              {group.localDay}
                            </TableCell>
                          )}
                          <TableCell>{row.campaignName}</TableCell>
                          <TableCell className="text-right tabular-nums">{a.runs}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{countsLabel(a.byTrigger)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{countsLabel(a.byOutcome)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMs(a.firstTextMs.median)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMs(a.clientDoneMs.median)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCount(a.inputTokens)} / {formatCount(a.outputTokens)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatCost(a.estimatedCostUsd)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {a.thumbsUp} / {a.thumbsDown}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{pushesLabel(a)}</TableCell>
                        </TableRow>
                      );
                    }),
                  )}
                </TableBody>
              </Table>
            )}
          </section>
        </>
      )}

      <ChargeQuestions from={from} to={to} campaignId={campaignId} />

      <TelemetryRunList campaignId={campaignId} from={effectiveFrom} to={effectiveTo} />
    </div>
  );
}
